/**
 * AppPreview.jsx
 * Interactive mobile app simulator with FULL backend integration
 * All data synchronized with the real customer portal
 */
import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';

// Import nutrition modals
import FoodSearchModal from './screens/FoodSearchModal';
import BarcodeScanner from './screens/BarcodeScanner';
import MobileLegalModal from './screens/MobileLegalModal';
import MealPhotoAnalyzer from './screens/MealPhotoAnalyzer';
import CreateMealModal from './screens/CreateMealModal';
import CreateProductWizard from './screens/CreateProductWizard';
import MealPlannerScreen from './screens/MealPlannerScreen';
import VoiceInputModal from './screens/VoiceInputModal';
import MealCalculatorModal from './screens/MealCalculatorModal';

// Import Effects
import SnowEffect from './SnowEffect';
import ConfettiEffect from './ConfettiEffect';
import CherryBlossomEffect from './CherryBlossomEffect';
import FireworksEffect from './FireworksEffect';
import WinterHat from './WinterHat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const lazyScreen = (importFn, exportName) => lazy(() =>
  importFn().then(module => ({ default: module[exportName] }))
);

const SplashScreen = lazyScreen(() => import('./screens/SplashScreen'), 'SplashScreen');
const LoginScreen = lazyScreen(() => import('./screens/LoginScreen'), 'LoginScreen');
const HomeScreen = lazyScreen(() => import('./screens/HomeScreen'), 'HomeScreen');
const NutritionScreen = lazyScreen(() => import('./screens/NutritionScreen'), 'NutritionScreen');
const BookingsScreen = lazyScreen(() => import('./screens/BookingsScreen'), 'BookingsScreen');
const BodyScreen = lazyScreen(() => import('./screens/BodyScreen'), 'BodyScreen');
const ProfileScreen = lazyScreen(() => import('./screens/ProfileScreen'), 'ProfileScreen');
const AccountScreen = lazyScreen(() => import('./screens/AccountScreen'), 'AccountScreen');
const ShopScreen = lazyScreen(() => import('./screens/ShopScreen'), 'ShopScreen');
const HealthScreen = lazyScreen(() => import('./screens/HealthScreen'), 'HealthScreen');
const SleepScreen = lazyScreen(() => import('./screens/SleepScreen'), 'SleepScreen');
const CommunityScreen = lazyScreen(() => import('./screens/CommunityScreen'), 'CommunityScreen');
const RecipesScreen = lazyScreen(() => import('./screens/RecipesScreen'), 'RecipesScreen');
const ShoppingScreen = lazyScreen(() => import('./screens/ShoppingListScreen'), 'ShoppingScreen');
const BookingNewScreen = lazyScreen(() => import('./screens/BookingNewScreen'), 'BookingNewScreen');
const WaitlistScreen = lazyScreen(() => import('./screens/WaitlistScreen'), 'WaitlistScreen');
const FitnessScreen = lazyScreen(() => import('./screens/FitnessScreen'), 'FitnessScreen');
const CustomerProfileScreen = lazyScreen(() => import('./screens/CustomerProfileScreen'), 'CustomerProfileScreen');
const HealthCoachScreen = lazyScreen(() => import('./screens/HealthCoachScreen'), 'HealthCoachScreen');
const DocumentsScreen = lazyScreen(() => import('./screens/DocumentsScreen'), 'DocumentsScreen');
const PeriodizationScreen = lazyScreen(() => import('./screens/PeriodizationScreen'), 'PeriodizationScreen');
const RecoveryScreen = lazyScreen(() => import('./screens/RecoveryScreen'), 'RecoveryScreen');
const TransactionHistoryScreen = lazyScreen(() => import('./screens/TransactionHistoryScreen'), 'TransactionHistoryScreen');
const NotificationSettingsScreen = lazyScreen(() => import('./screens/NotificationSettingsScreen'), 'NotificationSettingsScreen');
const SubscriptionsScreen = lazyScreen(() => import('./screens/SubscriptionsScreen'), 'SubscriptionsScreen');
const TrainingAnalyticsScreen = lazyScreen(() => import('./screens/TrainingAnalyticsScreen'), 'TrainingAnalyticsScreen');
const AppSettingsScreen = lazyScreen(() => import('./screens/AppSettingsScreen'), 'AppSettingsScreen');

const ScreenLoader = ({ label = 'Screen wird geladen...' }) => (
  <div className="flex items-center justify-center h-full" data-testid="mobile-preview-screen-loader">
    <div className="flex items-center gap-2">
      <RefreshCw className="w-6 h-6 animate-spin text-yellow-500" data-testid="mobile-preview-screen-loader-spinner" />
      <span className="text-sm text-zinc-300" data-testid="mobile-preview-screen-loader-label">{label}</span>
    </div>
  </div>
);

const screens = {
  splash: '🚀 Splash Screen',
  login: '🔐 Login',
  home: 'Dashboard',
  fitness: '💪 Fitness',
  nutrition: 'Ernährung',
  workout: 'Training',
  bookings: 'Termine',
  'booking-new': '📅 Termin buchen',
  account: 'Konto',
  shop: 'Shop',
  body: 'Körper',
  health: 'Health Connect',
  recipes: 'Rezepte',
  community: 'Community',
  profile: 'Mehr',
  shopping: 'Einkaufsliste',
  sleep: 'Schlaf',
  invoices: 'Rechnungen',
  'customer-profile': 'Kundenprofil',
  'health-coach': '✨ KI-Coach',
  'documents': '📄 Dokumente',
  'periodization': '📆 Periodisierung',
  'recovery': '🌙 Recovery',
  'transaction-history': '📊 Historie',
  'notification-settings': '🔔 Benachrichtigungen',
  'subscriptions': '🔄 Meine Abos',
  'training-analytics': '📊 Training Analytics',
  'settings': '⚙️ App-Einstellungen',
  'payment-methods': '💳 Zahlungsmethoden'
};

export const AppPreview = ({ token, appSettings }) => {
  const { companyName, name: brandNameRaw, companyLogo, logoUrl: tenantLogoUrl } = useTenantBranding();
  const brandLogo = appSettings?.branding?.logo_dark_url || appSettings?.branding?.logo_url || companyLogo || tenantLogoUrl || '/assets/invictafit-logo.png';
  const appName = appSettings?.branding?.app_name || appSettings?.app_name || companyName || brandNameRaw || '';
  const appSlogan = appSettings?.branding?.app_slogan || appSettings?.app_slogan || 'Dein Personal Training Studio';
  const [currentScreen, setCurrentScreen] = useState('splash');
  const [selectedTab, setSelectedTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  // Portal Token für API-Calls
  const [portalToken, setPortalToken] = useState(null);
  
  // Echte Kundendaten vom Backend
  const [customerData, setCustomerData] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pointsData, setPointsData] = useState({ balance: 0, credit_balance: 0, transactions: [] });
  const [packages, setPackages] = useState([]);
  const [bodyData, setBodyData] = useState({ entries: [], latest: null, progress: null });
  const [bodyLoading, setBodyLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [services, setServices] = useState([]);
  const [trainers, setTrainers] = useState([]);
  
  // Data loading state
  const [dataLoaded, setDataLoaded] = useState(false);
  const [adminPreviewLoaded, setAdminPreviewLoaded] = useState(false);
  const lastAdminTokenRef = useRef(null);
  
  // Legal acceptance state
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalStatus, setLegalStatus] = useState(null);
  
  // Nutrition modal states (for FAB menu)
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [showPhotoAnalyzer, setShowPhotoAnalyzer] = useState(false);
  const [showCreateMeal, setShowCreateMeal] = useState(false);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [createProductBarcode, setCreateProductBarcode] = useState('');
  const [showVoiceInput, setShowVoiceInput] = useState(false);  // NEW: Voice input
  const [showMealPlanner, setShowMealPlanner] = useState(false);  // NEW: Meal planner
  const [showMealCalculator, setShowMealCalculator] = useState(false);  // KI-Kalkulator
  const [nutritionMealType, setNutritionMealType] = useState('lunch');
  const [nutritionRefreshKey, setNutritionRefreshKey] = useState(0);
  
  // Get seasonal effect settings from props
  // Helper function to check if current date is in range
  const isInDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return false;
    const today = new Date();
    const currentMonthDay = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Handle year wrap (e.g., 12-01 to 02-28)
    if (startDate > endDate) {
      return currentMonthDay >= startDate || currentMonthDay <= endDate;
    }
    return currentMonthDay >= startDate && currentMonthDay <= endDate;
  };

  // Snow: Manual OR (Auto AND in date range)
  const snowEnabled = appSettings?.seasonal_effects?.snow_enabled || 
    (appSettings?.seasonal_effects?.snow_auto_enabled && 
     isInDateRange(appSettings?.seasonal_effects?.snow_start_date, appSettings?.seasonal_effects?.snow_end_date));
  const snowIntensity = appSettings?.seasonal_effects?.snow_intensity || 'medium';
  const snowSpeed = appSettings?.seasonal_effects?.snow_speed || 'medium';
  const winterHatEnabled = appSettings?.seasonal_effects?.winter_hat_enabled && snowEnabled;
  
  // Confetti: Manual OR (Auto AND in date range 31.12 - 02.01)
  const confettiEnabled = appSettings?.seasonal_effects?.confetti_enabled || 
    (appSettings?.seasonal_effects?.confetti_auto_enabled && 
     isInDateRange(appSettings?.seasonal_effects?.confetti_start_date || '12-31', 
                   appSettings?.seasonal_effects?.confetti_end_date || '01-02'));
  const confettiIntensity = appSettings?.seasonal_effects?.confetti_intensity || 'medium';
  
  // Cherry Blossom: Manual OR (Auto AND in date range 20.03 - 30.04)
  const cherryBlossomEnabled = appSettings?.seasonal_effects?.cherry_blossom_enabled || 
    (appSettings?.seasonal_effects?.cherry_blossom_auto_enabled && 
     isInDateRange(appSettings?.seasonal_effects?.cherry_blossom_start_date || '03-20', 
                   appSettings?.seasonal_effects?.cherry_blossom_end_date || '04-30'));
  const cherryBlossomIntensity = appSettings?.seasonal_effects?.cherry_blossom_intensity || 'medium';
  
  // Fireworks: Manual OR (Auto AND in date range 31.12 - 01.01)
  const fireworksEnabled = appSettings?.seasonal_effects?.fireworks_enabled || 
    (appSettings?.seasonal_effects?.fireworks_auto_enabled && 
     isInDateRange(appSettings?.seasonal_effects?.fireworks_start_date || '12-31', 
                   appSettings?.seasonal_effects?.fireworks_end_date || '01-01'));
  const fireworksIntensity = appSettings?.seasonal_effects?.fireworks_intensity || 'medium';

  // Check legal acceptance status
  const checkLegalStatus = async (customerToken) => {
    if (!customerToken) return;
    try {
      const response = await axios.get(`${API}/portal/auth/legal-check`, {
        headers: { Authorization: `Bearer ${customerToken}` }
      });
      setLegalStatus(response.data);
      if (response.data.needs_acceptance) {
        setShowLegalModal(true);
      }
    } catch (error) {
      console.error('Error checking legal status:', error);
    }
  };

  // Accept legal terms
  const handleAcceptLegal = async (acceptedCustomConditions = []) => {
    if (!portalToken) return;
    try {
      await axios.post(
        `${API}/portal/auth/accept-legal`,
        { accepted_custom_conditions: acceptedCustomConditions },
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      setShowLegalModal(false);
      setLegalStatus(null);
      toast.success('Bedingungen akzeptiert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  // Load admin preview data (recipes, challenges, leaderboard, services, trainers)
  useEffect(() => {
    const tokenKey = token || 'public';
    if (lastAdminTokenRef.current !== tokenKey) {
      lastAdminTokenRef.current = tokenKey;
      setAdminPreviewLoaded(false);
    }

    if (adminPreviewLoaded) {
      return;
    }

    loadAdminPreviewData();
  }, [token, adminPreviewLoaded]);

  const loadAdminPreviewData = async () => {
    try {
      // Use admin token for API calls if available
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [recipesRes, challengesRes, leaderboardRes, servicesRes, trainersRes] = await Promise.all([
        axios.get(`${API}/portal/recipes`, { headers }).catch(() => ({ data: { recipes: [] } })),
        axios.get(`${API}/portal/community/challenges`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/community/leaderboard`, { headers }).catch(() => ({ data: { leaderboard: [] } })),
        axios.get(`${API}/services?active_only=true`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/booking/trainers`, { headers }).catch(() => ({ data: [] })),
      ]);
      
      setRecipes(recipesRes.data.recipes || []);
      setChallenges(challengesRes.data || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setServices(servicesRes.data || []);
      setTrainers(trainersRes.data || []);
    } catch (error) {
      console.error('Error loading admin preview data:', error);
    } finally {
      setAdminPreviewLoaded(true);
      setLoading(false);
    }
  };

  // Load all customer data after login
  const loadCustomerData = useCallback(async (customerToken) => {
    if (!customerToken) return;
    
    const headers = { Authorization: `Bearer ${customerToken}` };
    
    try {
      const [
        appointmentsRes,
        upcomingRes,
        invoicesRes,
        pointsRes,
        packagesRes,
        servicesRes,
        trainersRes,
        bodyEntriesRes,
        bodyLatestRes,
        bodyProgressRes
      ] = await Promise.all([
        axios.get(`${API}/portal/appointments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/appointments/upcoming`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/invoices`, { headers }).catch(() => ({ data: { invoices: [] } })),
        axios.get(`${API}/portal/points`, { headers }).catch(() => ({ data: { balance: 0, credit_balance: 0, transactions: [] } })),
        axios.get(`${API}/portal/packages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/shop/services`, { headers }).catch(() => ({ data: { services: [] } })),
        axios.get(`${API}/portal/booking/trainers`, { headers }).catch(() => ({ data: [] })),
        // Body tracking APIs - correct paths
        axios.get(`${API}/portal/body/weight`, { headers }).catch(() => ({ data: { entries: [] } })),
        axios.get(`${API}/portal/body/measurements`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/body/progress`, { headers }).catch(() => ({ data: { has_data: false } })),
      ]);
      
      setAppointments(appointmentsRes.data || []);
      setUpcomingAppointments(upcomingRes.data || []);
      setInvoices(invoicesRes.data.invoices || invoicesRes.data || []);
      setPointsData(pointsRes.data);
      setPackages(packagesRes.data || []);
      setServices(servicesRes.data.services || servicesRes.data || []);
      setTrainers(trainersRes.data.trainers || trainersRes.data || []);
      
      // Set body tracking data
      // Weight entries are returned as an array with all body composition fields
      const weightEntries = Array.isArray(bodyEntriesRes.data) 
        ? bodyEntriesRes.data 
        : (bodyEntriesRes.data?.entries || []);
      const latestWeight = weightEntries.length > 0 ? weightEntries[0] : null;
      const measurements = bodyLatestRes.data || {};
      
      console.log('Body data loaded:', { latestWeight, measurements });
      
      setBodyData({
        entries: weightEntries,
        latest: {
          weight: latestWeight?.weight,
          bmi: latestWeight?.bmi,
          height: latestWeight?.height || measurements.height,
          // Body composition from latest weight entry
          muscle_mass: latestWeight?.muscle_mass,
          body_fat: latestWeight?.body_fat_percent || latestWeight?.body_fat,
          water_percent: latestWeight?.water_percent,
          visceral_fat: latestWeight?.visceral_fat
        },
        progress: bodyProgressRes.data
      });
      
      setDataLoaded(true);
      
      console.log('✅ All customer data loaded', { weightEntries, latestWeight });
    } catch (error) {
      console.error('Error loading customer data:', error);
    }
  }, []);

  // Refresh data
  const refreshData = async () => {
    if (portalToken) {
      toast.info('Daten werden aktualisiert...');
      await loadCustomerData(portalToken);
      toast.success('Daten aktualisiert');
    }
  };

  // Login handler
  const handleAppLogin = async (email, password) => {
    setLoginError('');
    try {
      // Portal-Login für Kunden
      const loginRes = await axios.post(`${API}/portal/auth/login`, { email, password });
      
      if (loginRes.data.token && loginRes.data.customer) {
        const customerToken = loginRes.data.token;
        setPortalToken(customerToken);
        
        setCustomerData({
          customer: loginRes.data.customer,
          points: loginRes.data.customer.points || 0,
          credit_balance: loginRes.data.customer.credit_balance || 0
        });
        
        // Load all customer data
        await loadCustomerData(customerToken);
        
        // Check legal acceptance status
        await checkLegalStatus(customerToken);
        
        toast.success(`Willkommen zurück, ${loginRes.data.customer.first_name}!`);
        setIsLoggedIn(true);
        setCurrentScreen('home');
        setSelectedTab('home');
        return true;
      }
      
      throw new Error('Ungültige Antwort');
    } catch (error) {
      console.error('Portal login error:', error);
      
      // Fallback: Admin/Trainer Login
      try {
        const adminLoginRes = await axios.post(`${API}/auth/login`, { email, password });
        if (adminLoginRes.data.access_token && adminLoginRes.data.user) {
          setCustomerData({
            customer: {
              id: adminLoginRes.data.user.id,
              first_name: adminLoginRes.data.user.first_name || email.split('@')[0],
              last_name: adminLoginRes.data.user.last_name || '',
              email: adminLoginRes.data.user.email
            },
            points: 0,
            credit_balance: 0
          });
          
          toast.success(`Angemeldet als ${adminLoginRes.data.user.role}`);
          setIsLoggedIn(true);
          setCurrentScreen('home');
          setSelectedTab('home');
          return true;
        }
      } catch (adminError) {
        console.error('Admin login also failed:', adminError);
      }
      
      const errorMsg = error.response?.data?.detail || 'Login fehlgeschlagen';
      setLoginError(errorMsg);
      return false;
    }
  };

  // Book appointment handler
  const handleBookAppointment = async (bookingData) => {
    if (!portalToken) {
      toast.error('Bitte zuerst anmelden');
      return false;
    }
    
    try {
      // Parse start_time to date and time if needed
      let requestData = { ...bookingData };
      
      if (bookingData.start_time && !bookingData.date) {
        // Extract date and time from start_time
        const startTime = bookingData.start_time;
        if (typeof startTime === 'string') {
          if (startTime.includes('T')) {
            const [datePart, timePart] = startTime.split('T');
            requestData.date = datePart;
            requestData.time = timePart.substring(0, 5);
          } else if (startTime.includes(' ')) {
            const [datePart, timePart] = startTime.split(' ');
            requestData.date = datePart;
            requestData.time = timePart.substring(0, 5);
          }
        }
        delete requestData.start_time;
      }
      
      // Keep notification preferences
      // Note: send_confirmation_email and send_whatsapp are supported by the backend
      
      const response = await axios.post(
        `${API}/portal/booking/book`,
        requestData,
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('Termin erfolgreich gebucht!');
      
      // Refresh appointments
      await loadCustomerData(portalToken);
      
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Buchung fehlgeschlagen');
      return false;
    }
  };

  // Add body tracking entry handler
  const handleAddBodyEntry = async (data) => {
    if (!portalToken) {
      toast.error('Bitte zuerst anmelden');
      return false;
    }
    
    setBodyLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('weight', data.weight);
      if (data.height) params.append('height', data.height);
      if (data.body_fat_percent) params.append('body_fat_percent', data.body_fat_percent);
      if (data.muscle_mass) params.append('muscle_mass', data.muscle_mass);
      
      await axios.post(
        `${API}/portal/body-tracking?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      // Refresh body data
      const headers = { Authorization: `Bearer ${portalToken}` };
      const [entriesRes, latestRes, progressRes] = await Promise.all([
        axios.get(`${API}/portal/body-tracking`, { headers }),
        axios.get(`${API}/portal/body-tracking/latest`, { headers }),
        axios.get(`${API}/portal/body-tracking/progress`, { headers }),
      ]);
      
      setBodyData({
        entries: entriesRes.data?.entries || [],
        latest: latestRes.data,
        progress: progressRes.data
      });
      
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
      return false;
    } finally {
      setBodyLoading(false);
    }
  };

  // Refresh body data
  const refreshBodyData = async () => {
    if (!portalToken) return;
    
    setBodyLoading(true);
    try {
      const headers = { Authorization: `Bearer ${portalToken}` };
      const [entriesRes, latestRes, progressRes] = await Promise.all([
        axios.get(`${API}/portal/body-tracking`, { headers }),
        axios.get(`${API}/portal/body-tracking/latest`, { headers }),
        axios.get(`${API}/portal/body-tracking/progress`, { headers }),
      ]);
      
      setBodyData({
        entries: entriesRes.data?.entries || [],
        latest: latestRes.data,
        progress: progressRes.data
      });
    } catch (error) {
      console.error('Error refreshing body data:', error);
    } finally {
      setBodyLoading(false);
    }
  };

  // Handle nutrition FAB actions - opens the correct modals
  const handleNutritionAction = (action) => {
    setNutritionMealType('lunch'); // Default meal type
    switch (action) {
      case 'photo':
        setShowPhotoAnalyzer(true);
        break;
      case 'search':
        setShowFoodSearch(true);
        break;
      case 'barcode':
        setShowBarcodeScanner(true);
        break;
      case 'create':
        setShowCreateMeal(true); // Open proper create meal modal
        break;
      case 'new_product':
        setShowCreateProduct(true); // Open 4-step product wizard
        break;
      case 'ai_help':
        setShowMealCalculator(true); // Open KI-Kalkulator (like Kundenportal)
        break;
      case 'voice':
        setShowVoiceInput(true); // NEW: Open voice input
        break;
      case 'planner':
        setShowMealPlanner(true); // NEW: Open meal planner
        break;
      default:
        break;
    }
  };
  
  // Handle nutrition food added - refresh nutrition screen
  const handleNutritionFoodAdded = useCallback(() => {
    // Increment refresh key to force NutritionScreen to reload
    setNutritionRefreshKey(prev => prev + 1);
  }, []);

  // Cancel appointment handler
  const handleCancelAppointment = async (appointmentId) => {
    if (!portalToken) return false;
    
    try {
      await axios.post(
        `${API}/portal/appointments/${appointmentId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success('Termin storniert');
      await loadCustomerData(portalToken);
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stornierung fehlgeschlagen');
      return false;
    }
  };

  // Helper functions
  const getCustomerFirstName = () => {
    if (customerData?.customer?.first_name) return customerData.customer.first_name;
    if (customerData?.customer?.name) return customerData.customer.name.split(' ')[0];
    return 'Kunde';
  };

  const getCustomerFullName = () => {
    if (customerData?.customer?.first_name && customerData?.customer?.last_name) {
      return `${customerData.customer.first_name} ${customerData.customer.last_name}`;
    }
    if (customerData?.customer?.name) return customerData.customer.name;
    return 'Kunde';
  };

  // Invoice payment handler
  const handlePayInvoice = async (invoice) => {
    if (!portalToken) {
      toast.error('Bitte zuerst anmelden');
      return;
    }
    
    toast.info(`💳 Rechnung ${invoice.invoice_number} - Zahlungsoptionen werden geladen...`);
    
    try {
      const response = await axios.post(
        `${API}/portal/invoices/${invoice.id}/pay`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      const data = response.data;
      
      if (data.payment_link) {
        // Open payment link in new tab
        toast.success('Zahlungsseite wird geöffnet...');
        window.open(data.payment_link, '_blank');
      } else if (data.can_pay_with_credit) {
        // Show credit payment option
        const confirmPay = window.confirm(
          `Mit Guthaben bezahlen?\n\nRechnung: CHF ${data.invoice_total.toFixed(2)}\nIhr Guthaben: CHF ${data.credit_balance.toFixed(2)}\n\nKlicken Sie OK um zu bezahlen.`
        );
        
        if (confirmPay) {
          const payResponse = await axios.post(
            `${API}/portal/invoices/${invoice.id}/pay-with-credit`,
            {},
            { headers: { Authorization: `Bearer ${portalToken}` } }
          );
          
          toast.success(`✅ ${payResponse.data.message}`);
          
          // Refresh data
          await loadCustomerData(portalToken);
        }
      } else {
        toast.info(data.message || 'Bitte kontaktieren Sie das Studio für Zahlungsoptionen');
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMsg = error.response?.data?.detail || 'Zahlung fehlgeschlagen';
      toast.error(errorMsg);
    }
  };

  // Invoice PDF viewer
  const handleViewInvoice = async (invoice) => {
    if (portalToken) {
      toast.info(`📄 PDF für ${invoice.invoice_number} wird geladen...`);
      // Open PDF in new tab
      window.open(`${API}/portal/invoices/${invoice.id}/pdf`, '_blank');
    } else {
      toast.error('Bitte zuerst anmelden');
    }
  };

  // Navigation handler
  const handleNavigate = (screen) => {
    setCurrentScreen(screen);
    if (['home', 'nutrition', 'bookings', 'body', 'profile'].includes(screen)) {
      setSelectedTab(screen);
    }
    setFabMenuOpen(false);
  };

  // Render current screen with real data
  const renderScreen = () => {
    switch (currentScreen) {
      case 'splash':
        return (
          <SplashScreen 
            onComplete={() => setCurrentScreen('login')} 
            winterHatEnabled={winterHatEnabled}
            splashVideoUrl={appSettings?.branding?.splash_video_url || '/assets/intro-video.mp4'}
            splashLogoSize={appSettings?.branding?.splash_logo_size || 280}
            splashFrameSize={appSettings?.branding?.splash_frame_size || 110}
            logoUrl={brandLogo}
            appName={appName}
            appSlogan={appSlogan}
          />
        );
      case 'login':
        return (
          <LoginScreen 
            onLogin={handleAppLogin} 
            loginError={loginError} 
            setLoginError={setLoginError} 
            winterHatEnabled={winterHatEnabled}
            logoUrl={brandLogo}
            logoSize={appSettings?.branding?.logo_size || 100}
            logoType="image"
          />
        );
      case 'home':
        return (
          <HomeScreen 
            customerName={getCustomerFirstName()} 
            onNavigate={handleNavigate}
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            nextAppointment={upcomingAppointments[0]}
            packages={packages}
            winterHatEnabled={winterHatEnabled}
          />
        );
      case 'nutrition':
        return (
          <NutritionScreen 
            key={nutritionRefreshKey}
            onNavigate={handleNavigate} 
            portalToken={portalToken}
            customerId={customerData?.id}
          />
        );
      case 'bookings':
        return (
          <BookingsScreen 
            appointments={appointments}
            upcomingAppointments={upcomingAppointments}
            onCancelAppointment={handleCancelAppointment}
            onRefresh={refreshData}
            portalToken={portalToken}
          />
        );
      case 'body':
        return (
          <BodyScreen 
            bodyData={bodyData}
            onAddEntry={handleAddBodyEntry}
            onRefresh={refreshBodyData}
            loading={bodyLoading}
            portalToken={portalToken}
          />
        );
      case 'profile':
        return (
          <ProfileScreen 
            customerFirstName={getCustomerFirstName()} 
            customerFullName={getCustomerFullName()} 
            customerPoints={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            onNavigate={handleNavigate} 
          />
        );
      case 'account':
        return (
          <AccountScreen 
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            transactions={pointsData.transactions}
            packages={packages}
            invoices={invoices}
            onNavigate={handleNavigate}
            onPayInvoice={handlePayInvoice}
            onViewInvoice={handleViewInvoice}
          />
        );
      case 'invoices':
        return (
          <AccountScreen 
            points={pointsData.balance}
            creditBalance={pointsData.credit_balance}
            transactions={pointsData.transactions}
            packages={packages}
            invoices={invoices}
            onNavigate={handleNavigate}
            onPayInvoice={handlePayInvoice}
            onViewInvoice={handleViewInvoice}
            initialTab="invoices"
          />
        );
      case 'shop':
        return (
          <ShopScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'health':
        return <HealthScreen />;
      case 'customer-profile':
        return (
          <CustomerProfileScreen 
            customer={customerData?.customer || customerData}
            paymentMethods={[]}
            onBack={() => setCurrentScreen('profile')}
            onAddPaymentMethod={() => toast.info('Zahlungsmethode hinzufügen - In Entwicklung')}
            onDeletePaymentMethod={() => toast.info('Zahlungsmethode entfernen - In Entwicklung')}
            onSetDefaultPaymentMethod={() => toast.info('Standard setzen - In Entwicklung')}
            portalToken={portalToken}
            onProfileUpdate={() => {
              // Refresh customer data after profile update
              if (portalToken) {
                axios.get(`${API}/portal/auth/login`, {
                  headers: { Authorization: `Bearer ${portalToken}` }
                }).catch(() => {});
              }
            }}
          />
        );
      case 'payment-methods':
        return (
          <CustomerProfileScreen 
            customer={customerData?.customer || customerData}
            paymentMethods={[]}
            onBack={() => setCurrentScreen('profile')}
            onAddPaymentMethod={() => toast.info('Zahlungsmethode hinzufügen - In Entwicklung')}
            onDeletePaymentMethod={() => toast.info('Zahlungsmethode entfernen - In Entwicklung')}
            onSetDefaultPaymentMethod={() => toast.info('Standard setzen - In Entwicklung')}
            portalToken={portalToken}
          />
        );
      case 'fitness':
        return (
          <FitnessScreen 
            portalToken={portalToken}
            customerData={customerData}
            bodyData={bodyData}
            onAddBodyEntry={() => toast.info('Körperdaten können in den Einstellungen hinzugefügt werden')}
            onBack={() => setCurrentScreen('home')}
          />
        );
      case 'sleep':
        return <SleepScreen />;
      case 'community':
        return (
          <CommunityScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
          />
        );
      case 'recipes':
        return (
          <RecipesScreen 
            portalToken={portalToken}
            onNavigate={handleNavigate}
          />
        );
      case 'shopping':
        return <ShoppingScreen />;
      case 'booking-new':
        return (
          <BookingNewScreen 
            onBack={() => setCurrentScreen('bookings')}
            services={services}
            trainers={trainers}
            portalToken={portalToken}
            onBook={handleBookAppointment}
            customerData={customerData}
          />
        );
      case 'waitlist':
        return (
          <WaitlistScreen 
            onBack={() => setCurrentScreen('bookings')} 
            portalToken={portalToken}
            services={services}
          />
        );
      case 'health-coach':
        return (
          <HealthCoachScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'documents':
        return (
          <DocumentsScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'periodization':
        return (
          <PeriodizationScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'recovery':
        return (
          <RecoveryScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'transaction-history':
        return (
          <TransactionHistoryScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'notification-settings':
        return (
          <NotificationSettingsScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'subscriptions':
        return (
          <SubscriptionsScreen 
            portalToken={portalToken}
            customerId={customerData?.customer?.id}
            onNavigate={handleNavigate}
          />
        );
      case 'training-analytics':
        return (
          <TrainingAnalyticsScreen 
            portalToken={portalToken}
            onNavigate={handleNavigate}
          />
        );
      case 'settings':
        return (
          <AppSettingsScreen 
            portalToken={portalToken}
            onBack={() => handleNavigate('profile')}
            onNavigate={handleNavigate}
          />
        );
      default:
        return <HomeScreen customerName={getCustomerFirstName()} onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="flex gap-8 items-start">
      {/* Phone Preview */}
      <PhoneFrame 
        showFab={currentScreen === 'nutrition' || currentScreen === 'bookings'}
        fabMenuOpen={fabMenuOpen}
        setFabMenuOpen={setFabMenuOpen}
        hideNav={currentScreen === 'splash' || currentScreen === 'login'}
        currentScreen={currentScreen}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        setCurrentScreen={handleNavigate}
        onNutritionAction={handleNutritionAction}
        winterHatEnabled={winterHatEnabled}
      >
        {/* Screen Content */}
        <div className="relative h-full" style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Seasonal Effects - Background Layer (z-index: 1) - MUST be behind content */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1, pointerEvents: 'none' }}>
            {snowEnabled && <SnowEffect intensity={snowIntensity} speed={snowSpeed} enabled={snowEnabled} />}
            {confettiEnabled && <ConfettiEffect intensity={confettiIntensity} enabled={confettiEnabled} />}
            {cherryBlossomEnabled && <CherryBlossomEffect intensity={cherryBlossomIntensity} enabled={cherryBlossomEnabled} />}
            {fireworksEnabled && <FireworksEffect intensity={fireworksIntensity} enabled={fireworksEnabled} />}
          </div>
          
          {/* Screen Content - Foreground Layer (z-index: 10) */}
          <div className="relative h-full overflow-y-auto" style={{ position: 'relative', zIndex: 10 }}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
              </div>
            ) : (
              <Suspense fallback={<ScreenLoader />}> 
                {renderScreen()}
              </Suspense>
            )}
          </div>
        </div>
        
        {/* Nutrition Modals - INSIDE PhoneFrame for proper containment */}
        {showFoodSearch && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <FoodSearchModal 
              isOpen={showFoodSearch}
              onClose={() => setShowFoodSearch(false)}
              onSelectFood={handleNutritionFoodAdded}
              onCreateProduct={(barcode) => {
                setShowFoodSearch(false);
                setCreateProductBarcode(barcode || '');
                setShowCreateProduct(true);
              }}
              portalToken={portalToken}
              mealType={nutritionMealType}
            />
          </div>
        )}
        
        {showBarcodeScanner && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <BarcodeScanner 
              isOpen={showBarcodeScanner}
              onClose={() => setShowBarcodeScanner(false)}
              onProductFound={handleNutritionFoodAdded}
              onCreateProduct={(barcode) => {
                setShowBarcodeScanner(false);
                setCreateProductBarcode(barcode || '');
                setShowCreateProduct(true);
              }}
              portalToken={portalToken}
              mealType={nutritionMealType}
            />
          </div>
        )}
        
        {showPhotoAnalyzer && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <MealPhotoAnalyzer 
              isOpen={showPhotoAnalyzer}
              onClose={() => setShowPhotoAnalyzer(false)}
              onMealAdded={handleNutritionFoodAdded}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
              mealType={nutritionMealType}
            />
          </div>
        )}
        
        {showCreateMeal && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <CreateMealModal 
              isOpen={showCreateMeal}
              onClose={() => setShowCreateMeal(false)}
              onMealCreated={handleNutritionFoodAdded}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
            />
          </div>
        )}
        
        {showCreateProduct && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <CreateProductWizard 
              isOpen={showCreateProduct}
              onClose={() => {
                setShowCreateProduct(false);
                setCreateProductBarcode('');
              }}
              onProductCreated={handleNutritionFoodAdded}
              portalToken={portalToken}
              initialBarcode={createProductBarcode}
            />
          </div>
        )}
        
        {showMealPlanner && (
          <div className="absolute inset-0 z-50 bg-black/90 flex items-center justify-center" style={{ top: '48px', bottom: '80px' }}>
            <div className="w-full h-full bg-zinc-900 overflow-y-auto">
              <MealPlannerScreen
                portalToken={portalToken}
                customerId={customerData?.customer?.id}
                onClose={() => setShowMealPlanner(false)}
              />
            </div>
          </div>
        )}
        
        {showVoiceInput && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <VoiceInputModal
              isOpen={showVoiceInput}
              onClose={() => setShowVoiceInput(false)}
              portalToken={portalToken}
              onFoodAdded={handleNutritionFoodAdded}
            />
          </div>
        )}

        {showMealCalculator && (
          <div className="absolute inset-0 z-50" style={{ top: '48px', bottom: '80px' }}>
            <MealCalculatorModal
              isOpen={showMealCalculator}
              onClose={() => setShowMealCalculator(false)}
              portalToken={portalToken}
              customerId={customerData?.customer?.id}
              onMealAdded={handleNutritionFoodAdded}
            />
          </div>
        )}
      </PhoneFrame>
      
      {/* Screen Navigator & Info - Right Panel */}
      <div className="flex-1 space-y-6">
        {/* Screen Navigator */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <h2 className="text-gray-900 font-semibold text-lg mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-yellow-500" />
            Screen Navigation
          </h2>
          <p className="text-gray-500 text-sm mb-4">
            Klicken Sie auf einen Screen, um die Vorschau zu ändern
          </p>
          
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(screens).map(([id, label]) => (
              <button
                key={id}
                onClick={() => handleNavigate(id)}
                className={`px-4 py-3 rounded-lg text-left transition-colors ${
                  currentScreen === id
                    ? 'bg-yellow-500 text-black font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Customer Data Info */}
        {isLoggedIn && (
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-gray-900 font-semibold text-lg">Kundendaten (Live)</h2>
              <button 
                onClick={refreshData}
                className="px-3 py-1.5 bg-yellow-500 text-black text-sm rounded-lg hover:bg-yellow-400 flex items-center gap-1"
              >
                <RefreshCw className="w-4 h-4" />
                Aktualisieren
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Kunde</span>
                <span className="text-gray-900 font-medium">{getCustomerFullName()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Punktestand</span>
                <span className="text-yellow-400 font-medium">{pointsData.balance} Punkte</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Guthaben</span>
                <span className="text-green-400 font-medium">CHF {pointsData.credit_balance?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Termine</span>
                <span className="text-gray-900">{appointments.length} gesamt, {upcomingAppointments.length} kommend</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Rechnungen</span>
                <span className="text-gray-900">{invoices.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Aktive Pakete</span>
                <span className="text-gray-900">{packages.length}</span>
              </div>
            </div>
          </div>
        )}

        {/* API Status */}
        <div className={`rounded-xl p-6 border ${dataLoaded ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30' : 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-500/30'}`}>
          <div className="flex items-center gap-3">
            {dataLoaded ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <AlertCircle className="w-6 h-6 text-yellow-500" />
            )}
            <div>
              <h3 className="text-gray-900 font-semibold">
                {dataLoaded ? 'Live-Daten synchronisiert' : 'Bitte anmelden'}
              </h3>
              <p className="text-gray-500 text-sm">
                {dataLoaded 
                  ? 'Alle Daten werden vom echten Backend geladen'
                  : 'Melden Sie sich an, um echte Kundendaten zu sehen'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Test Credentials */}
        <div className="bg-gray-100 rounded-xl p-4 border border-gray-200">
          <p className="text-gray-500 text-xs mb-2">Test-Zugangsdaten:</p>
          <code className="text-yellow-400 text-xs">kunde@test.de / test123</code>
        </div>
      </div>
    </div>
  );
};

export default AppPreview;
